var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import jwtDecode from "jwt-decode";
import { getAccessToken } from "@elixir/auth";
import { getAppConfig } from "../services";
// Function to fetch roles from user token.
export const getConfig = () => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    // Retry logic to wait for token to be available after authentication
    let token = yield getAccessToken();
    let retries = 0;
    const maxRetries = 10;
    const delay = 300;
    while (!token && retries < maxRetries) {
        yield new Promise(resolve => setTimeout(resolve, delay)); // Wait 300ms
        token = yield getAccessToken();
        retries++;
    }
    // Check if authentication has failed
    if (retries === maxRetries && !token) {
        throw new Error("Access token not available after maximum retries. Authentication may have failed.");
    }
    const decoded = jwtDecode(token);
    const config = yield getAppConfig();
    return {
        roles: (_a = decoded === null || decoded === void 0 ? void 0 : decoded.roles) !== null && _a !== void 0 ? _a : [],
        name: decoded === null || decoded === void 0 ? void 0 : decoded.name,
        config: config,
    };
});
// Function to push app config to session storage.
// Data is fetched from Azure app config.
export const pushAppConfigToSessionStorage = (appConfig) => {
    if (!appConfig) {
        console.warn("App config is null or undefined, skipping session storage update");
        return;
    }
    Object.entries(appConfig).forEach(([key, value]) => {
        if (typeof value !== "string") {
            return;
        }
        sessionStorage.setItem(key, value);
    });
    // Environment-aware backend URL configuration
    const currentEnv = process.env.NODE_ENV;
    const isLocalDev = currentEnv === "development";
    const backendUrl = appConfig.globalFrontdoorbaseurl;
    const webpackUrl = process.env.FRONTDOOR_BASEURL;
    if (currentEnv === undefined) {
        console.warn("NODE_ENV is undefined, defaulting to production settings.");
    }
    if (isLocalDev && webpackUrl) {
        sessionStorage.setItem("globalFrontdoorbaseurl", webpackUrl);
        console.log(`Environment: ${currentEnv} | DEV: ${webpackUrl}${backendUrl && backendUrl !== webpackUrl ? " (overridden with FRONTDOOR_BASEURL from webpack.config.dev.js)" : ""}`);
    }
    else if (backendUrl) {
        console.log(`Environment: ${currentEnv} | API: ${backendUrl}`);
    }
    else {
        console.warn("Backend URL is undefined");
    }
};
// Function to read data from session storage.
export const getDataFromSessionStorage = (key) => {
    return sessionStorage.getItem(key) || "";
};
/**
 * Allowed backend hostname suffixes. Backend URLs are delivered from Azure App
 * Configuration, but session storage is client-writable. This allowlist
 * provides defense-in-depth against session storage tampering (e.g., via XSS).
 */
const ALLOWED_BACKEND_HOSTNAME_SUFFIXES = [
    ".azurewebsites.net",
    ".azurefd.net",
    ".services.microsoft.com", // Production (gethelp services)
];
/**
 * Validates a backend URL from session storage is safe to send requests to.
 * Enforces HTTPS protocol and checks the hostname against an allowlist of
 * known Microsoft-controlled domain suffixes.
 *
 * @param url - The raw URL string from session storage
 * @returns Validation result with the sanitized origin (scheme + host) on
 *          success, or a human-readable reason on failure
 */
export const validateBackendUrl = (url) => {
    if (!url) {
        return { valid: false, reason: "Backend URL is not configured." };
    }
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch (_a) {
        return { valid: false, reason: "Backend URL is malformed." };
    }
    // In local development mode, allow any well-formed URL (including
    // http://localhost with non-standard ports) so that developers can
    // point at local backends via FRONTDOOR_BASEURL in webpack.config.dev.js.
    if (process.env.NODE_ENV === "development") {
        return { valid: true, origin: parsed.origin };
    }
    if (parsed.protocol !== "https:") {
        return { valid: false, reason: "Backend URL must use HTTPS." };
    }
    // URL.port is "" when the URL uses the default port for the scheme.
    // Allow explicit :443 to avoid rejecting configs like https://host:443.
    if (parsed.port !== "" && parsed.port !== "443") {
        return {
            valid: false,
            reason: "Backend URL must not specify a non-standard port.",
        };
    }
    const hostname = parsed.hostname.toLowerCase();
    const isAllowed = ALLOWED_BACKEND_HOSTNAME_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
    if (!isAllowed) {
        // Defense-in-depth: log when the allowlist blocks a URL so tampering
        // attempts are observable. configHelper already uses console.warn for
        // config issues (see pushAppConfigToSessionStorage).
        console.warn(`[validateBackendUrl] Rejected hostname "${hostname}" — not in allowlist.`);
        return {
            valid: false,
            reason: "Backend URL hostname is not in the allowlist.",
        };
    }
    return { valid: true, origin: parsed.origin };
};
// Function to delete data from session storage.
export const deleteDataFromSessionStorage = (key) => {
    sessionStorage.removeItem(key);
};
